import axios from 'axios';
import { API_BASE } from '@/config/api';
import { clearClientOnboardingDrafts } from '@/lib/proveRequestForm';

// Create axios instance
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Token management
let authToken = localStorage.getItem('proovam_token');

export const setAuthToken = (token) => {
  authToken = token;
  if (token) {
    localStorage.setItem('proovam_token', token);
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    localStorage.removeItem('proovam_token');
    delete api.defaults.headers.common['Authorization'];
  }
};

// Initialize token from storage
if (authToken) {
  api.defaults.headers.common['Authorization'] = `Bearer ${authToken}`;
}

const isPublicAuthRequest = (config) => {
  const url = config?.url || '';
  return (
    url.includes('/auth/client/login') ||
    url.includes('/auth/client/register') ||
    url.includes('/auth/brm/request-otp') ||
    url.includes('/auth/brm/verify-otp') ||
    url.includes('/auth/brm/login-pin') ||
    url.includes('/auth/firebase/session')
  );
};

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 429 && error.config?.url?.includes('/messaging/')) {
      error.message = 'Too many chat requests. Please wait a moment before trying again.';
    }
    if (error.response?.status === 401 && !isPublicAuthRequest(error.config)) {
      // Expired/invalid session on protected routes — not failed login attempts
      setAuthToken(null);
      clearClientOnboardingDrafts();
      localStorage.removeItem('proovam_user_type');
      localStorage.removeItem('user_id');
      window.location.href = '/';
    }
    return Promise.reject(error);
  }
);

// ============== AUTH API ==============

export const authAPI = {
  getConfig: () => api.get('/auth/config').then((res) => res.data),

  firebaseSession: (body) =>
    api.post('/auth/firebase/session', body).then((res) => res.data),

  linkBrmPhone: (body) =>
    api.post('/auth/firebase/brm/link-phone', body).then((res) => res.data),

  // Legacy BRM Auth
  requestOTP: (phone, referralCode = null) =>
    api.post('/auth/brm/request-otp', { phone, referral_code: referralCode }),
  verifyOTP: (phone, otp, referralCode = null) =>
    api.post('/auth/brm/verify-otp', { phone, otp, referral_code: referralCode }),
  setPIN: (pin) => api.post('/auth/brm/set-pin', { pin }),
  loginWithPIN: (phone, pin) => api.post('/auth/brm/login-pin', { phone, pin }),

  // Legacy Client Auth
  registerClient: (name, email, password) =>
    api.post('/auth/client/register', { name, email, password }),
  loginClient: (email, password) => api.post('/auth/client/login', { email, password }),
};

// ============== BRM API ==============

export const brmAPI = {
  // Profile
  getProfile: () => api.get('/brm/me'),
  updateProfile: (data) => api.put('/brm/me', data),
  completeOnboarding: () => api.post('/brm/onboarding/complete'),

  // Joined campaigns (multi-campaign enrollment)
  getJoinedCampaigns: () => api.get('/brm/joined-campaigns').then((res) => res.data),

  // Referrals
  getReferrals: () => api.get('/brm/referrals'),

  // Products — face-to-face funded offers
  getProducts: () => api.get('/brm/products'),
  getReferralProducts: () =>
    api.get('/brm/prove-requests/referral').then((res) => res.data),

  // Dashboard
  getDashboard: (productId = null) =>
    api.get('/brm/dashboard', { params: { product_id: productId } }),

  // Enrollments
  createEnrollment: (phone, productId, notes = null, campaignId = null, userIdentifierType = null, registrationData = null) =>
    api.post('/brm/enrollments', {
      phone,
      product_id: productId,
      notes,
      campaign_id: campaignId,
      user_identifier_type: userIdentifierType,
      registration_data: registrationData,
    }),
  getEnrollments: (status = null, productId = null, limit = 50, offset = 0) =>
    api.get('/brm/enrollments', { params: { status, product_id: productId, limit, offset } }),

  // Campaign hub (per-campaign operating view)
  getCampaignHub: (campaignId) => api.get(`/brm/campaigns/${campaignId}/hub`),
  getCampaignWorkspace: (campaignId) => api.get(`/brm/campaigns/${campaignId}/workspace`).then((res) => res.data),
  getCampaignEnrollments: (campaignId, status = null, limit = 50, offset = 0) =>
    api.get(`/brm/campaigns/${campaignId}/enrollments`, {
      params: { status, limit, offset },
    }),

  // Wallet
  getWallet: () => api.get('/brm/wallet'),
  getTransactions: (limit = 50, offset = 0) =>
    api.get('/brm/wallet/transactions', { params: { limit, offset } }),
  requestWithdrawal: (amount, bankName, accountNumber, accountName) =>
    api.post('/brm/wallet/withdraw', {
      amount,
      bank_name: bankName,
      account_number: accountNumber,
      account_name: accountName
    }),
};

// ============== PRODUCT API (Client) ==============

export const productAPI = {
  getProducts: () => api.get('/products/'),
  createProduct: (data) => api.post('/products/', data),
  getProduct: (productId) => api.get(`/products/${productId}`),
  updateProduct: (productId, data) => api.put(`/products/${productId}`, data),
  regenerateKey: (productId) => api.post(`/products/${productId}/regenerate-key`),
  deleteProduct: (productId) => api.delete(`/products/${productId}`),
  uploadIcon: (productId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/products/${productId}/icon`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// ============== PROJECT API (Client) ==============

export const projectAPI = {
  getProjects: () => api.get('/projects/'),
  createProject: (data) => api.post('/projects/', data),
  updateProject: (projectId, data) => api.patch(`/projects/${projectId}`, data),
  transitionProject: (projectId, status) => api.post(`/projects/${projectId}/transition`, { status }),
  listContracts: (projectId) => api.get(`/projects/${projectId}/integration-contracts`),
  saveContract: (projectId, data) => api.post(`/projects/${projectId}/integration-contracts`, data),
  bindContract: (projectId, contractId, campaignId) =>
    api.post(`/projects/${projectId}/integration-contracts/${contractId}/bind`, { campaignId }),
};

// ============== INTEGRATION API (Client) ==============

const mapAuthTypeForApi = (authType) => {
  if (authType === 'bearer' || authType === 'oauth') return 'jwt';
  if (!authType || authType === 'none') return null;
  return authType;
};

export const integrationAPI = {
  getIntegration: (productId) => api.get(`/products/${productId}/integration`),
  updateIntegration: (productId, data) => api.put(`/products/${productId}/integration`, data),
  saveIntegration: (productId, data) => api.put(`/products/${productId}/integration`, data),
  testBaseConnection: (productId, data) => api.post(`/products/${productId}/integration/test-base`, data),
  testBaseUrl: (productId, baseUrl, authType = 'none', authValue = null) =>
    api.post(`/products/${productId}/integration/test-base`, {
      baseUrl,
      authType: mapAuthTypeForApi(authType),
      authValue,
    }),
  testEndpoint: (productId, data) =>
    api.post(`/products/${productId}/integration/test-endpoint`, {
      baseUrl: data.baseUrl ?? data.base_url,
      authType: mapAuthTypeForApi(data.authType ?? data.auth_type),
      authValue: data.authValue ?? data.auth_value ?? null,
      path: data.path,
      method: data.method,
      headers: data.headers,
      body: data.body,
      params: data.params ?? data.query_params,
    }),
};

// ============== WEBHOOK API (Client) ==============

export const webhookAPI = {
  getWebhookLogs: (productId = null, limit = 50, offset = 0) => 
    api.get('/client/webhook-logs', { params: { product_id: productId, limit, offset } }),
};

// ============== SYNC API (Client) ==============

export const syncAPI = {
  triggerSync: (productId) => api.post(`/sync/trigger/${productId}`),
  triggerSyncAll: () => api.post('/sync/trigger-all'),
};

// ============== CLIENT API ==============

export const clientAPI = {
  // Profile
  getProfile: () => api.get('/client/me'),
  completeOnboarding: () => api.post('/client/onboarding/complete'),
  setActiveProduct: (productId) => api.put(`/client/active-product/${productId}`),

  // Dashboard
  getDashboard: (productId = null) =>
    api.get('/client/dashboard', { params: { product_id: productId } }),

  getEnhancedAnalytics: (productId = null) =>
    api.get('/client/analytics/enhanced', { params: { product_id: productId } }),

  getGrowthROI: (productId = null) =>
    api.get('/client/analytics/growth-roi', { params: { product_id: productId } }),
  // BRMs
  getBRMs: (status = null, limit = 50, offset = 0) =>
    api.get('/client/brms', { params: { status, limit, offset } }),
  getMessagingContacts: () => api.get('/client/messaging-contacts'),

  // Enrollments
  getEnrollments: (status = null, brmId = null, productId = null, limit = 50, offset = 0) =>
    api.get('/client/enrollments', { params: { status, brm_id: brmId, product_id: productId, limit, offset } }),

  // Analytics
  getSubscriptionAnalytics: (productId = null) =>
    api.get('/client/analytics/subscriptions', { params: { product_id: productId } }),

  // Settings / compatibility helpers
  getSettings: () => api.get('/client/settings'),
  regenerateAPIKey: () => api.post('/client/api-key/regenerate'),
  updateAPISettings: (apiEndpoint = null, webhookUrl = null) =>
    api.put('/client/settings', { api_endpoint: apiEndpoint, webhook_url: webhookUrl }),
  getWebhookLogs: (limit = 50, eventType = null, offset = 0, productId = null) =>
    api.get('/client/webhook-logs', {
      params: { product_id: productId, event_type: eventType, limit, offset },
    }),
  getPaymentIntent: (reference) => api.get(`/client/payment-intents/${reference}`),
};

// ============== DEMO API ==============

export const demoAPI = {
  activateUser: (enrollmentId) =>
    api.post('/demo/activate-user', null, { params: { enrollment_id: enrollmentId } }),
};

// ============== ADMIN API ==============

// Create a separate axios instance for admin with admin_token
const adminApi = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Admin token management
let adminToken = localStorage.getItem('admin_token');

export const setAdminToken = (token) => {
  adminToken = token;
  if (token) {
    localStorage.setItem('admin_token', token);
    adminApi.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    localStorage.removeItem('admin_token');
    delete adminApi.defaults.headers.common['Authorization'];
  }
};

// Initialize admin token from storage
if (adminToken) {
  adminApi.defaults.headers.common['Authorization'] = `Bearer ${adminToken}`;
}

const isAdminAuthRequest = (config) => {
  const url = config?.url || '';
  return url.includes('/admin/login');
};

// Admin response interceptor
adminApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && !isAdminAuthRequest(error.config)) {
      setAdminToken(null);
      window.location.href = '/admin/login';
    }
    return Promise.reject(error);
  }
);

export const adminAPI = {
  // Auth
  login: (email, password) => adminApi.post('/admin/login', { email, password }),
  inspectStaffInvitation: (token) => adminApi.post('/admin/staff-invitations/accept/inspect', { token }),
  acceptStaffInvitation: (token, password) => adminApi.post('/admin/staff-invitations/accept', { token, password }),
  requestPasswordRecovery: (email) => adminApi.post('/admin/password-recovery/request', { email }),
  resetPassword: (token, password) => adminApi.post('/admin/password-recovery/reset', { token, password }),
  createStaffInvitation: (payload) => adminApi.post('/admin/staff-invitations', payload),
  listStaffInvitations: () => adminApi.get('/admin/staff-invitations'),
  revokeStaffInvitation: (invitationId) => adminApi.post(`/admin/staff-invitations/${invitationId}/revoke`),
  // Profile
  getProfile: () => adminApi.get('/admin/me'),
  
  // Dashboard
  getDashboard: () => adminApi.get('/admin/dashboard'),
  
  // Clients
  getClients: (status = null, limit = 50, offset = 0) =>
    adminApi.get('/admin/clients', { params: { status, limit, offset } }),
  getClient: (clientId) => adminApi.get(`/admin/clients/${clientId}`),
  getClientDetail: (clientId) => adminApi.get(`/admin/clients/${clientId}`),
  updateClientStatus: (clientId, status) =>
    adminApi.put(`/admin/clients/${clientId}/status`, { status }),
  
  // BRMs
  getBRMs: (status = null, limit = 50, offset = 0) =>
    adminApi.get('/admin/brms', { params: { status, limit, offset } }),
  getBrms: (status = null, limit = 50, offset = 0) =>
    adminApi.get('/admin/brms', { params: { status, limit, offset } }),
  
  // Enrollments
  getEnrollments: (status = null, clientId = null, brmId = null, limit = 50, offset = 0) =>
    adminApi.get('/admin/enrollments', { 
      params: { status, client_id: clientId, brm_id: brmId, limit, offset } 
    }),
  
  // Analytics
  getAnalytics: () => adminApi.get('/admin/analytics'),

  // Operations (secret-free configuration snapshot)
  getOperationsConfig: () => adminApi.get('/admin/operations/config'),
  getProviderSettings: () => adminApi.get('/admin/operations/provider-settings'),
  updatePaystackSettings: (payload) => adminApi.put('/admin/operations/provider-settings/paystack', payload),
  updateSmtpSettings: (payload) => adminApi.put('/admin/operations/provider-settings/smtp', payload),
  activateProviderSettings: (configId, payload) => adminApi.post(`/admin/operations/provider-settings/${configId}/activate`, payload),
  acknowledgeProviderRollout: (configId, payload) => adminApi.post(`/admin/operations/provider-settings/${configId}/acknowledge-rollout`, payload),

  // Staff and RBAC
  getStaff: (search = null, status = null, limit = 50, offset = 0) =>
    adminApi.get('/admin/staff', { params: { search, status, limit, offset } }),
  getStaffRoles: () => adminApi.get('/admin/staff/roles'),
  updateStaffRoles: (adminId, roles, reason) =>
    adminApi.put(`/admin/staff/${adminId}/roles`, { roles, reason }),
  updateStaffStatus: (adminId, status, reason) =>
    adminApi.put(`/admin/staff/${adminId}/status`, { status, reason }),
};

// ============== HEALTH CHECK ==============

export const healthCheck = () => api.get('/health');

export default api;


// ============== Messaging API ==============
export const messagingAPI = {
  // Conversations
  listConversations: (page = 1, pageSize = 25) => api.get('/messaging/conversations', {
    params: { page, page_size: pageSize },
  }).then(res => res.data),
  createConversation: (data) => api.post('/messaging/conversations', data).then(res => res.data),
  
  // Messages
  getMessages: (conversationId, limit = 50, before = null) =>
    api.get(`/messaging/conversations/${conversationId}/messages`, {
      params: { limit, before }
    }).then(res => res.data),
  sendMessage: (conversationId, data) =>
    api.post(`/messaging/conversations/${conversationId}/messages`, data).then((res) => res.data),
  
  listCalls: (conversationId) => api.get(`/messaging/conversations/${conversationId}/calls`).then((res) => res.data),
  getCall: (callId) => api.get(`/messaging/call-sessions/${callId}`).then((res) => res.data),

  // Mark as read
  markAsRead: (conversationId) =>
    api.post(`/messaging/conversations/${conversationId}/mark-read`).then(res => res.data),
  
  // Upload URL
  getUploadUrl: (fileName, fileSize, conversationId) =>
    api.get('/messaging/upload-url', {
      params: { file_name: fileName, file_size: fileSize, conversation_id: conversationId }
    }).then(res => res.data),
  getIceServers: (callId) => api.get('/messaging/calls/ice-servers', {
    params: { call_id: callId },
  }).then((res) => res.data),
  downloadAttachment: (message) => {
    const value = new URL(message.content, window.location.origin);
    const marker = '/api/messaging/';
    const markerIndex = value.pathname.indexOf(marker);
    if (!['http:', 'https:'].includes(value.protocol) || markerIndex < 0) {
      return Promise.reject(new Error('Attachment URL is invalid'));
    }
    const relativePath = value.pathname.slice(markerIndex + '/api'.length) + value.search;
    return api.get(relativePath, { responseType: 'blob' }).then((res) => res.data);
  },
};


// ============== Social API ==============
export const socialAPI = {
  // Available niches
  getAvailableNiches: () => api.get('/social/niches/available').then(res => res.data),
  
  // Profiles
  linkProfile: (data) => api.post('/social/profiles', data).then(res => res.data),
  listProfiles: () => api.get('/social/profiles').then(res => res.data),
  unlinkProfile: (platform) => api.delete(`/social/profiles/${platform}`).then(res => res.data),
  
  // Niches
  setNiches: (data) => api.post('/social/niches', data).then(res => res.data),
  getMyNiches: () => api.get('/social/niches').then(res => res.data)
};



// ============== Campaign API ==============
export const campaignAPI = {
  // List campaigns (BRM sees active, Client sees theirs)
  listCampaigns: (statusOrOptions = null, page = 1, pageSize = 20) => {
    const params = { page, page_size: pageSize };
    if (typeof statusOrOptions === 'object' && statusOrOptions !== null) {
      const opts = statusOrOptions;
      params.page = opts.page ?? 1;
      params.page_size = opts.pageSize ?? opts.page_size ?? 20;
      if (opts.status) params.status = opts.status;
      if (opts.product_id) params.product_id = opts.product_id;
      if (opts.request_type) params.request_type = opts.request_type;
      if (opts.direct_version) params.direct_version = opts.direct_version;
      if (typeof opts.audience_pay === 'boolean') params.audience_pay = opts.audience_pay;
      if (opts.category) params.category = opts.category;
      if (opts.geography) params.geography = opts.geography;
      if (opts.payout_type) params.payout_type = opts.payout_type;
      if (opts.product_type) params.product_type = opts.product_type;
      if (opts.q) params.q = opts.q;
    } else if (statusOrOptions) {
      params.status = statusOrOptions;
    }
    return api.get('/campaigns', { params }).then(res => res.data);
  },

  // Get single campaign
  getCampaign: (campaignId) => api.get(`/campaigns/${campaignId}`).then(res => res.data),

  // Join campaign (BRM only)
  joinCampaign: (campaignId) => api.post(`/campaigns/${campaignId}/join`).then(res => res.data),

  // Get campaign stats (Client only)
  getCampaignStats: (campaignId) => api.get(`/campaigns/${campaignId}/stats`).then(res => res.data),

  // Create campaign (Client only)
  createCampaign: (data) => api.post('/campaigns', data).then(res => res.data),

  // Publish campaign (Client only)
  publishCampaign: (campaignId) => api.put(`/campaigns/${campaignId}/publish`).then(res => res.data),

  // Campaign creative assets (Client only)
  // Backend: GET/POST /campaigns/{id}/assets
  getAssets: (campaignId) =>
    api.get(`/campaigns/${campaignId}/assets`).then((res) => res.data),
  createAsset: (campaignId, data) =>
    api.post(`/campaigns/${campaignId}/assets`, data).then((res) => res.data),

  // Enhanced founder/Agent setup
  getSetup: (campaignId) =>
    api.get(`/campaigns/${campaignId}/setup`).then((res) => res.data),
  updateSetup: (campaignId, data) =>
    api.put(`/campaigns/${campaignId}/setup`, data).then((res) => res.data),
  saveRegistrationForm: (campaignId, fields) =>
    api.put(`/campaigns/${campaignId}/registration-form`, { fields }).then((res) => res.data),
  listContracts: (campaignId) =>
    api.get(`/campaigns/${campaignId}/contracts`).then((res) => res.data),
  saveContract: (campaignId, data) =>
    api.put(`/campaigns/${campaignId}/contracts`, data).then((res) => res.data),
  testContract: (campaignId, contractId) =>
    api.post(`/campaigns/${campaignId}/contracts/${contractId}/test`).then((res) => res.data),
  createAudienceSnapshot: (campaignId, data) =>
    api.post(`/campaigns/${campaignId}/audience-snapshots`, data).then((res) => res.data),
  listNegotiations: (campaignId, brmId = null) =>
    api.get(`/campaigns/${campaignId}/negotiations`, {
      params: brmId ? { brm_id: brmId } : {},
    }).then((res) => res.data),
  proposeNegotiation: (campaignId, data) =>
    api.post(`/campaigns/${campaignId}/negotiations`, data).then((res) => res.data),
  acceptNegotiation: (campaignId, brmId) =>
    api.post(`/campaigns/${campaignId}/negotiations/${brmId}/accept`).then((res) => res.data),
  acceptNegotiationVersion: (campaignId, cardVersionId) =>
    api.post(`/campaigns/${campaignId}/negotiation-versions/${cardVersionId}/accept`).then((res) => res.data),
  rejectNegotiationVersion: (campaignId, cardVersionId) =>
    api.post(`/campaigns/${campaignId}/negotiation-versions/${cardVersionId}/reject`).then((res) => res.data),
  withdrawNegotiationVersion: (campaignId, cardVersionId) =>
    api.post(`/campaigns/${campaignId}/negotiation-versions/${cardVersionId}/withdraw`).then((res) => res.data),
};

// ============== Referral / Attribution API ==============
export const referralAPI = {
  // All referral links for authenticated BRM
  getMyLinks: () => api.get('/referral/my-links').then(res => res.data),

  // Single campaign referral link
  getCampaignLink: (campaignId) => api.get(`/referral/link/${campaignId}`).then(res => res.data),

  // Stats for a campaign referral link (includes pending_in_window)
  getCampaignStats: (campaignId) => api.get(`/referral/stats/${campaignId}`).then(res => res.data),

  // Public link stats by token
  getLinkStats: (token) => api.get(`/attribution/link-stats/${token}`).then(res => res.data),
};

// ============== Payments / Escrow API ==============
export const paymentsAPI = {
  fundCampaign: (campaignId, amount) =>
    api.post(`/payments/fund/${campaignId}`, { amount }).then((res) => res.data),
  devFundCampaign: (campaignId, amount) =>
    api.post(`/payments/dev/fund/${campaignId}`, { amount }).then((res) => res.data),
  topupCampaign: (campaignId, amount) =>
    api.post(`/payments/topup/${campaignId}`, { amount }).then((res) => res.data),
  getEscrow: (campaignId) =>
    api.get(`/payments/escrow/${campaignId}`).then((res) => res.data),
};

// ============== Proof Engine API ==============
export const proofAPI = {
  getProof: (proofId) => api.get(`/proof/${proofId}`).then((res) => res.data),
  getClientLedger: (campaignId = null, limit = 50) =>
    api.get('/proof/client/ledger', { params: { campaign_id: campaignId, limit } }).then((res) => res.data),
  getBrmLedger: (limit = 50) =>
    api.get('/proof/brm/ledger', { params: { limit } }).then((res) => res.data),
  getBrmScores: () => api.get('/proof/brm/scores').then((res) => res.data),
};

// ============== Navigator (BRM Copilot) API ==============
export const navigatorAPI = {
  getBriefing: () => api.get('/navigator/briefing'),
  getRecommendedCampaigns: (limit = 10) =>
    api.get('/navigator/campaigns/recommended', { params: { limit } }),
  getConversationPrep: (campaignId) => api.get(`/navigator/prep/${campaignId}`),
  coachContent: (channel, draftMessage, campaignId = null) =>
    api.post('/navigator/coach/content', {
      channel,
      draft_message: draftMessage,
      campaign_id: campaignId,
    }),
  getSuggestedFollowUps: () => api.get('/navigator/follow-ups/suggested'),
  createFollowUp: (body) => api.post('/navigator/follow-ups', body),
  completeFollowUp: (followUpId) => api.post(`/navigator/follow-ups/${followUpId}/complete`),
};

// ============== Premium Packages API ==============
export const packagesAPI = {
  listTiers: () => api.get('/packages/tiers'),
  getSubscription: () => api.get('/packages/subscription'),
  selectTier: (tier) => api.post('/packages/subscription/select', { tier }),
};
